// @ts-nocheck
/**
 * Add Payment Dialog Component
 *
 * Collects payment details and dispatches createPayment action.
 * Tenant context is handled by the HTTP client (X-Tenant-Id header).
 * SAP sync (including BP number resolution) happens on the backend via afterCreate lifecycle.
 * Reference number is auto-generated on the backend (paymentNumber).
 */

import { useState, useEffect } from 'react'
import { useSelector, useDispatch, useForm, zodResolver, z } from '@spcc/core'
import { useTranslation } from '@spcc/core'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Loader2,
} from '@spcc/uikit'

import { financeActions } from '../../store/finance.slice'
import { selectCreatePaymentLoading, selectCreatePaymentError } from '../../store/finance.selectors'
import type { PaymentMethod } from '../../domain/types'

const PAYMENT_METHODS: { value: PaymentMethod; labelKey: string }[] = [
  { value: 'bank_transfer', labelKey: 'finance.methods.bank_transfer' },
  { value: 'cash', labelKey: 'finance.methods.cash' },
  { value: 'check', labelKey: 'finance.methods.check' },
  { value: 'viban', labelKey: 'finance.methods.viban' },
  { value: 'sadad', labelKey: 'finance.methods.sadad' },
  { value: 'credit_card', labelKey: 'finance.methods.credit_card' },
]

const paymentSchema = z.object({
  amount: z.string().min(1, 'Amount is required').refine(
    (val) => !isNaN(Number(val)) && Number(val) > 0,
    'Amount must be greater than 0'
  ),
  paymentMethod: z.string().min(1, 'Payment method is required'),
})

interface AddPaymentDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function AddPaymentDialog({ open, onOpenChange }: AddPaymentDialogProps) {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const loading = useSelector(selectCreatePaymentLoading)
  const error = useSelector(selectCreatePaymentError)

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(paymentSchema),
    defaultValues: {
      amount: '',
      paymentMethod: '',
    },
  })

  const selectedMethod = watch('paymentMethod')
  const [submitted, setSubmitted] = useState(false)

  // Close dialog on success
  useEffect(() => {
    if (submitted && !loading && !error) {
      onOpenChange(false)
      reset()
      setSubmitted(false)
    }
  }, [submitted, loading, error, onOpenChange, reset])

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      reset()
      setSubmitted(false)
    }
  }, [open, reset])

  const onSubmit = (data: any) => {
    setSubmitted(true)
    dispatch(financeActions.createPayment({
      amount: Number(data.amount),
      paymentMethod: data.paymentMethod as PaymentMethod,
      paymentDate: new Date().toISOString(),
    }))
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-strong border-border/50 max-w-md">
        <DialogHeader>
          <DialogTitle>{t('finance.makePayment', 'Make Payment')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 mt-4">
          <div>
            <Label>{t('finance.paymentAmount', 'Amount')}</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              placeholder={t('finance.amountPlaceholder', '0.00')}
              className="mt-1.5 bg-background/50"
              {...register('amount')}
            />
            {errors.amount && (
              <p className="text-sm text-destructive mt-1">{errors.amount.message as string}</p>
            )}
          </div>

          <div>
            <Label>{t('finance.paymentMethod', 'Payment Method')}</Label>
            <Select
              value={selectedMethod}
              onValueChange={(val) => setValue('paymentMethod', val, { shouldValidate: true })}
            >
              <SelectTrigger className="mt-1.5 bg-background/50">
                <SelectValue placeholder={t('finance.selectMethod', 'Select payment method')} />
              </SelectTrigger>
              <SelectContent>
                {PAYMENT_METHODS.map((m) => (
                  <SelectItem key={m.value} value={m.value}>
                    {t(m.labelKey, m.value)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.paymentMethod && (
              <p className="text-sm text-destructive mt-1">{errors.paymentMethod.message as string}</p>
            )}
          </div>

          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-gradient-to-r from-primary to-primary/90"
            >
              {loading && <Loader2 className="w-4 h-4 me-2 animate-spin" />}
              {t('finance.confirmPayment', 'Confirm Payment')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
